import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  FormGroup,
  FormHelperText,
  FormSelect,
  FormSelectOption,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminSplaCategories } from '../../../hooks/useAdminSplaCatalog';
import { DECIMAL_RE } from '../../../utils/money';
import type {
  SplaAgreementType,
  SplaLicense,
  SplaLicenseCreateRequest,
  SplaLicenseUpdateRequest,
  SplaOffering,
} from '../../../types/models/Spla';
import type { ApiResponseSplaLicense } from '../../../types/api/ApiResponseSpla';

const PART_NUMBER_MAX = 20;
const ITEM_NAME_MAX = 120;
const PRODUCT_FAMILY_MAX = 160;
const REMARKS_MAX = 255;

// Backend field-error keys rendered inline on the matching input; anything else (body, id, …)
// lands in the form-level alert.
const FIELD_ERROR_KEYS = new Set([
  'spla_category_id',
  'part_number',
  'item_name',
  'product_family',
  'agreement_type',
  'offering',
  'unit_price',
  'remarks',
  'sort_order',
]);

interface SplaLicenseModalProps {
  isOpen: boolean;
  /** `null` = create; a license = edit (parent keys the mount so state is always fresh). */
  license: SplaLicense | null;
  onClose: () => void;
  /** Hand the saved row back: `isCreate` tells the grid to refetch (sheet position) vs patch in place. */
  onSaved: (license: SplaLicense, isCreate: boolean) => void;
}

/**
 * Create or edit one SPLA catalog license. Create never offers `archived` — a license is never born
 * retired (the API rejects it); retirement lives on the grid's kebab. Identity is the tuple
 * (part_number, agreement_type, offering) — the backend re-checks uniqueness on any change.
 */
function SplaLicenseModal({ isOpen, license, onClose, onSaved }: SplaLicenseModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const isEdit = license !== null;

  const { categories, loading: categoriesLoading, error: categoriesError } = useAdminSplaCategories(isOpen);

  const [categoryId, setCategoryId] = useState<string>(license !== null ? String(license.category.id) : '');
  const [partNumber, setPartNumber] = useState(license?.part_number ?? '');
  const [itemName, setItemName] = useState(license?.item_name ?? '');
  const [productFamily, setProductFamily] = useState(license?.product_family ?? '');
  const [agreementType, setAgreementType] = useState<SplaAgreementType>(license?.agreement_type ?? 'Corporate');
  const [offering, setOffering] = useState<SplaOffering>(license?.offering ?? 'Standard');
  const [unitPrice, setUnitPrice] = useState(license?.unit_price ?? '');
  const [remarks, setRemarks] = useState(license?.remarks ?? '');
  const [sortOrder, setSortOrder] = useState(license !== null ? String(license.sort_order) : '');

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const clearFieldError = (key: string) => {
    setFieldErrors((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // The Corporate sheet has no offering column — Corporate rows are always "Standard".
  const effectiveOffering: SplaOffering = agreementType === 'Corporate' ? 'Standard' : offering;

  const trimmedPartNumber = partNumber.trim();
  const trimmedItemName = itemName.trim();
  const trimmedProductFamily = productFamily.trim();
  const trimmedUnitPrice = unitPrice.trim();
  const trimmedRemarks = remarks.trim();
  const trimmedSortOrder = sortOrder.trim();

  const isPartNumberValid = trimmedPartNumber.length > 0 && trimmedPartNumber.length <= PART_NUMBER_MAX;
  const isItemNameValid = trimmedItemName.length > 0 && trimmedItemName.length <= ITEM_NAME_MAX;
  const isProductFamilyValid = trimmedProductFamily.length > 0 && trimmedProductFamily.length <= PRODUCT_FAMILY_MAX;
  const isUnitPriceValid = DECIMAL_RE.test(trimmedUnitPrice);
  const isRemarksValid = trimmedRemarks.length <= REMARKS_MAX;
  const isSortOrderValid = trimmedSortOrder === '' || /^\d+$/.test(trimmedSortOrder);
  const isCategoryValid = categoryId !== '';

  const isFormValid =
    isCategoryValid && isPartNumberValid && isItemNameValid && isProductFamilyValid && isUnitPriceValid && isRemarksValid && isSortOrderValid;

  // PATCH is partial and an empty change-set is rejected — Save stays disabled until something
  // actually differs from the loaded row.
  const buildUpdateBody = (original: SplaLicense): SplaLicenseUpdateRequest => {
    const body: SplaLicenseUpdateRequest = {};
    if (Number(categoryId) !== original.category.id) {
      body.spla_category_id = Number(categoryId);
    }
    if (trimmedPartNumber !== original.part_number) {
      body.part_number = trimmedPartNumber;
    }
    if (trimmedItemName !== original.item_name) {
      body.item_name = trimmedItemName;
    }
    if (trimmedProductFamily !== original.product_family) {
      body.product_family = trimmedProductFamily;
    }
    if (agreementType !== original.agreement_type) {
      body.agreement_type = agreementType;
    }
    if (effectiveOffering !== original.offering) {
      body.offering = effectiveOffering;
    }
    if (trimmedUnitPrice !== original.unit_price) {
      body.unit_price = trimmedUnitPrice;
    }
    if (trimmedRemarks !== (original.remarks ?? '')) {
      body.remarks = trimmedRemarks === '' ? null : trimmedRemarks;
    }
    if (trimmedSortOrder !== '' && Number(trimmedSortOrder) !== original.sort_order) {
      body.sort_order = Number(trimmedSortOrder);
    }
    return body;
  };

  const hasChanges = license === null || Object.keys(buildUpdateBody(license)).length > 0;
  const canSubmit = !isSaving && isFormValid && hasChanges;

  const buildCreateBody = (): SplaLicenseCreateRequest => {
    const body: SplaLicenseCreateRequest = {
      spla_category_id: Number(categoryId),
      part_number: trimmedPartNumber,
      item_name: trimmedItemName,
      product_family: trimmedProductFamily,
      unit_price: trimmedUnitPrice,
      agreement_type: agreementType,
      offering: effectiveOffering,
    };
    if (trimmedRemarks !== '') {
      body.remarks = trimmedRemarks;
    }
    if (trimmedSortOrder !== '') {
      body.sort_order = Number(trimmedSortOrder);
    }
    return body;
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setFieldErrors({});
    setFormError(null);
    try {
      const response = await apiFetch(license !== null ? `/admin/spla/licenses/${license.id}` : '/admin/spla/licenses', {
        method: license !== null ? 'PATCH' : 'POST',
        body: JSON.stringify(license !== null ? buildUpdateBody(license) : buildCreateBody()),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseSplaLicense | null;
      if (body?.status === 'success' && body.data?.license) {
        const saved = body.data.license;
        onSaved(saved, !isEdit);
        addNotification({
          title: isEdit ? 'License updated' : 'License created',
          srTitle: 'Success alert',
          description: `${saved.part_number} (${saved.agreement_type}) — ${saved.product_family}`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onClose();
        return;
      }

      // Split the field-keyed validation map: known keys inline, the rest (incl. the cross-field
      // pseudo-field `body`) in the form-level alert.
      const errors: Record<string, string> = {};
      const generalParts: string[] = [];
      if (body?.data) {
        for (const [key, value] of Object.entries(body.data)) {
          if (typeof value !== 'string') {
            continue;
          }
          if (FIELD_ERROR_KEYS.has(key)) {
            errors[key] = value;
          } else {
            generalParts.push(value);
          }
        }
      }
      const general =
        generalParts.length > 0
          ? generalParts.join(' ')
          : Object.keys(errors).length === 0
            ? (body?.message ?? 'Could not save the license. Please try again.')
            : null;
      setFieldErrors(errors);
      setFormError(general);
      addNotification({
        title: isEdit ? 'Could not update the license' : 'Could not create the license',
        srTitle: 'Error alert',
        description: general ?? Object.values(errors)[0] ?? 'Please review the highlighted fields.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setFormError(message);
      addNotification({
        title: isEdit ? 'Could not update the license' : 'Could not create the license',
        srTitle: 'Error alert',
        description: message,
        variant: 'danger',
      });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  const fieldHelper = (key: string, fallback?: string) =>
    (fieldErrors[key] !== undefined || fallback !== undefined) && (
      <FormHelperText>
        <HelperText>
          <HelperTextItem variant={fieldErrors[key] !== undefined ? 'error' : 'default'}>
            {fieldErrors[key] ?? fallback}
          </HelperTextItem>
        </HelperText>
      </FormHelperText>
    );

  return (
    <Modal
      variant="medium"
      isOpen={isOpen}
      onClose={onClose}
      aria-label={isEdit ? `Edit license ${license.part_number}` : 'Create license'}
    >
      <ModalHeader
        title={isEdit ? `Edit license — ${license.part_number}` : 'Create license'}
        description={
          isEdit
            ? 'Identity (part number, agreement, offering) is re-checked for uniqueness; a distributor price edit affects future reports only.'
            : 'Adds a row to the distributor reporting-sheet catalog.'
        }
      />
      <ModalBody>
        <Form
          id="spla-license-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) {
              void handleSubmit();
            }
          }}
        >
          {formError !== null && (
            <Alert variant="danger" isInline title={isEdit ? 'Update failed' : 'Creation failed'}>
              {formError}
            </Alert>
          )}
          {categoriesError !== null && (
            <Alert variant="warning" isInline title="Could not load the categories">
              {categoriesError}
            </Alert>
          )}

          <FormGroup label="Category" isRequired fieldId="spla-license-category">
            <FormSelect
              id="spla-license-category"
              value={categoryId}
              onChange={(_event, value) => {
                setCategoryId(value);
                clearFieldError('spla_category_id');
              }}
              aria-label="Sheet category"
              isDisabled={isSaving || categoriesLoading}
            >
              <FormSelectOption value="" label={categoriesLoading ? 'Loading categories…' : 'Select a category'} isDisabled />
              {categories.map((category) => (
                <FormSelectOption key={category.id} value={String(category.id)} label={category.name} />
              ))}
            </FormSelect>
            {fieldHelper('spla_category_id')}
          </FormGroup>

          <FormGroup label="Part number" isRequired fieldId="spla-license-part-number">
            <TextInput
              id="spla-license-part-number"
              type="text"
              value={partNumber}
              placeholder="9EM-00562"
              onChange={(_event, value) => {
                setPartNumber(value);
                clearFieldError('part_number');
              }}
              validated={
                fieldErrors['part_number'] !== undefined || trimmedPartNumber.length > PART_NUMBER_MAX
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving}
            />
            {fieldHelper(
              'part_number',
              trimmedPartNumber.length > PART_NUMBER_MAX
                ? `At most ${PART_NUMBER_MAX} characters.`
                : 'The Microsoft part number, verbatim.',
            )}
          </FormGroup>

          <FormGroup label="Item name" isRequired fieldId="spla-license-item-name">
            <TextInput
              id="spla-license-item-name"
              type="text"
              value={itemName}
              placeholder="WinSvrSTDCore ALNG LicSAPk 2Lic CoreLic"
              onChange={(_event, value) => {
                setItemName(value);
                clearFieldError('item_name');
              }}
              validated={
                fieldErrors['item_name'] !== undefined || trimmedItemName.length > ITEM_NAME_MAX ? 'error' : 'default'
              }
              isDisabled={isSaving}
            />
            {fieldHelper(
              'item_name',
              trimmedItemName.length > ITEM_NAME_MAX ? `At most ${ITEM_NAME_MAX} characters.` : undefined,
            )}
          </FormGroup>

          <FormGroup label="Product family" isRequired fieldId="spla-license-product-family">
            <TextInput
              id="spla-license-product-family"
              type="text"
              value={productFamily}
              placeholder="Windows Server Standard (2 Core License)"
              onChange={(_event, value) => {
                setProductFamily(value);
                clearFieldError('product_family');
              }}
              validated={
                fieldErrors['product_family'] !== undefined || trimmedProductFamily.length > PRODUCT_FAMILY_MAX
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving}
            />
            {fieldHelper(
              'product_family',
              trimmedProductFamily.length > PRODUCT_FAMILY_MAX ? `At most ${PRODUCT_FAMILY_MAX} characters.` : undefined,
            )}
          </FormGroup>

          <FormGroup label="Agreement type" fieldId="spla-license-agreement">
            <FormSelect
              id="spla-license-agreement"
              value={agreementType}
              onChange={(_event, value) => {
                setAgreementType(value as SplaAgreementType);
                clearFieldError('agreement_type');
              }}
              aria-label="Price list (agreement type)"
              isDisabled={isSaving}
            >
              <FormSelectOption value="Corporate" label="Corporate" />
              <FormSelectOption value="Academic" label="Academic" />
            </FormSelect>
            {fieldHelper('agreement_type')}
          </FormGroup>

          <FormGroup label="Offering" fieldId="spla-license-offering">
            <FormSelect
              id="spla-license-offering"
              value={effectiveOffering}
              onChange={(_event, value) => {
                setOffering(value as SplaOffering);
                clearFieldError('offering');
              }}
              aria-label="Academic offering"
              isDisabled={isSaving || agreementType === 'Corporate'}
            >
              <FormSelectOption value="Standard" label="Standard" />
              <FormSelectOption value="Student" label="Student" />
            </FormSelect>
            {fieldHelper(
              'offering',
              agreementType === 'Corporate' ? 'Corporate rows are always Standard.' : 'The Academic sheet sub-column.',
            )}
          </FormGroup>

          <FormGroup label="Distributor (Econocom) price" isRequired fieldId="spla-license-unit-price">
            <TextInput
              id="spla-license-unit-price"
              type="text"
              inputMode="decimal"
              value={unitPrice}
              placeholder="5.5000"
              onChange={(_event, value) => {
                setUnitPrice(value);
                clearFieldError('unit_price');
              }}
              validated={
                fieldErrors['unit_price'] !== undefined || (trimmedUnitPrice !== '' && !isUnitPriceValid)
                  ? 'error'
                  : 'default'
              }
              isDisabled={isSaving}
              aria-label="Distributor unit price in euros, excluding VAT"
            />
            {fieldHelper(
              'unit_price',
              trimmedUnitPrice !== '' && !isUnitPriceValid
                ? 'Enter a non-negative amount with up to 4 decimal places.'
                : 'Per unit and per month, EUR excl. VAT — what Ancalagon is charged, never shown to tenants.',
            )}
          </FormGroup>

          <FormGroup label="Remarks" fieldId="spla-license-remarks">
            <TextInput
              id="spla-license-remarks"
              type="text"
              value={remarks}
              placeholder="New price 01/01/2026"
              onChange={(_event, value) => {
                setRemarks(value);
                clearFieldError('remarks');
              }}
              validated={fieldErrors['remarks'] !== undefined || !isRemarksValid ? 'error' : 'default'}
              isDisabled={isSaving}
            />
            {fieldHelper(
              'remarks',
              !isRemarksValid
                ? `At most ${REMARKS_MAX} characters.`
                : `Optional sheet annotation.${isEdit ? ' Clearing it removes the annotation.' : ''}`,
            )}
          </FormGroup>

          <FormGroup label="Sort order" fieldId="spla-license-sort-order">
            <TextInput
              id="spla-license-sort-order"
              type="text"
              inputMode="numeric"
              value={sortOrder}
              onChange={(_event, value) => {
                setSortOrder(value);
                clearFieldError('sort_order');
              }}
              validated={fieldErrors['sort_order'] !== undefined || !isSortOrderValid ? 'error' : 'default'}
              isDisabled={isSaving}
              aria-label="Row position inside the category section"
            />
            {fieldHelper(
              'sort_order',
              !isSortOrderValid
                ? 'Enter a non-negative whole number.'
                : 'Optional — leave empty to append at the end of the section.',
            )}
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          type="submit"
          form="spla-license-form"
          isDisabled={!canSubmit}
          isLoading={isSaving}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default SplaLicenseModal;
